import type { ReactNode } from "react"

import type { ProposalFinancialSummaryDto } from "~/api/dto/proposal-financial-summary.dto"
import type {
  SolarFinancedAmountDto,
  SolarFinancingSummaryDto,
} from "~/api/dto/solar-proposal-pricing.dto"

import { SectionNotice } from "./SectionNotice"
import { SOLAR_LIGHTREACH_LENDER } from "./solar-lightreach-copy"
import type {
  SolarPresentationPricing,
  SolarSectionState,
} from "./solar-presentation-data"
import {
  TPO_SECTION_EYEBROW,
  TPO_SECTION_HEADING,
  TPO_SECTION_HEADING_ACCENT,
  TPO_TERMS_PENDING_HEADING,
  TPO_TERMS_PENDING_NOTICE,
} from "./solar-tpo-copy"
import {
  SolarSungageLoanTerms,
  solarSungagePaymentFootnote,
} from "./SolarSungageLoanTerms"
import { SolarTpoCard } from "./SolarTpoCard"
import {
  GradText,
  Panel,
  Section,
  SectionHeading,
} from "../BatteryOnlyProposal/primitives"
import {
  FinancingTermsLine,
  financingTermsLabel,
  LedgerPayment,
} from "../BatteryOnlyProposal/sections/financial-summary-card"
import { formatUsdExact } from "../BatteryOnlyProposal/view-helpers"
import {
  financialSummaryLedgerLines,
  FinancialSummaryIncentives,
  hasFinancialSummaryIncentives,
} from "../ProposalPresentation/pricing/financial-summary-ledger"
import {
  LedgerFooter,
  type LedgerLine,
  LedgerRows,
  NetSystemCostFooter,
  reduction,
  SummaryCardLayout,
  SummaryHeading,
} from "../ProposalPresentation/pricing/summary-card"
import {
  STALE_FIGURES_NOTE,
  STALE_FIGURES_WITHIN_CLASS,
} from "../ProposalPresentation/updates/stale-figure"

export const PRICING_NOT_AVAILABLE_NOTICE = `Pricing isn't available yet`

/** V1's Credit Human labels for the figure that closes the ledger. */
export const FINANCED_AMOUNT_LABELS: Record<
  SolarFinancedAmountDto[`kind`],
  string
> = {
  financed_amount: `Your financed amount`,
  loan_amount: `Loan amount`,
}

export const PROPEL_DISCOUNT_LABEL = `Propel discount`

const SECTION_ID = `investment`

/**
 * The stored ledger, then Concert's Propel discount (V1's: the price Concert
 * doesn't finance) as the server computed it, before the financed amount.
 */
const solarLedgerLines = (
  summary: ProposalFinancialSummaryDto,
  financingSummary: SolarFinancingSummaryDto | null,
): LedgerLine[] => {
  const lines = financialSummaryLedgerLines(summary)
  const propelDiscount = financingSummary?.propelDiscount
  if (propelDiscount == null) return lines
  return [
    ...lines,
    {
      label: PROPEL_DISCOUNT_LABEL,
      badge: `DISCOUNT`,
      value:
        propelDiscount > 0
          ? reduction(propelDiscount)
          : formatUsdExact(propelDiscount),
    },
  ]
}

/**
 * The investment section: the stored price's summary on Battery's investment
 * card. The pricing read loads and fails on its own, so every other section
 * stays rendered whatever happens here, and rendering never prices. The seller
 * page marks an out-of-date price `stale`; its Updates panel refreshes it.
 */
export const SolarPricingSection = ({
  state,
  stale = false,
  lightReachPricing = null,
  action = null,
}: {
  state: SolarSectionState<SolarPresentationPricing>
  /** Seller page only: underline the figures as out of date. */
  stale?: boolean
  /** Seller page only: Get LightReach pricing, beside missing LightReach terms. */
  lightReachPricing?: ReactNode
  /** Seller page only: Reserve now, beneath the card's figures. */
  action?: ReactNode
}): JSX.Element => {
  if (state.status !== `ready`) {
    return <SectionNotice id={SECTION_ID} state={state} noun="pricing" />
  }

  const { pricingStatus, summary } = state.data
  // A gateway that predates Credit Human sends no financing at all.
  const financingSummary = state.data.financingSummary ?? null
  // A TPO (LightReach) has no system price for the homeowner: its terms and
  // disclosures replace the ledger, whatever summary the read carries. The
  // plan decides, not the terms: a LightReach plan without them still never
  // shows the ledger.
  const tpo = financingSummary?.terms?.tpo ?? null
  const priceSetByOffer =
    state.data.priceSetByOffer === true ||
    tpo != null ||
    financingSummary?.lender === SOLAR_LIGHTREACH_LENDER

  if (priceSetByOffer && (tpo == null || financingSummary == null)) {
    return (
      <Section id={SECTION_ID}>
        <SectionHeading eyebrow={TPO_SECTION_EYEBROW}>
          {TPO_TERMS_PENDING_HEADING}
        </SectionHeading>
        <Panel className="flex flex-col items-center gap-4 px-6 py-10 text-center">
          <p
            role="status"
            data-testid="solar-tpo-terms-pending"
            className="text-[length:var(--step--1)] text-bp-text-muted"
          >
            {/* Only the seller is told what to do; anyone else sees no price yet. */}
            {lightReachPricing != null
              ? TPO_TERMS_PENDING_NOTICE
              : PRICING_NOT_AVAILABLE_NOTICE}
          </p>
          {lightReachPricing}
        </Panel>
      </Section>
    )
  }

  if (tpo != null && financingSummary != null) {
    return (
      <Section id={SECTION_ID}>
        <SectionHeading eyebrow={TPO_SECTION_EYEBROW}>
          {TPO_SECTION_HEADING[tpo.kind]},{` `}
          <GradText>{TPO_SECTION_HEADING_ACCENT}</GradText>.
        </SectionHeading>
        <SolarTpoCard
          terms={tpo}
          financingSummary={financingSummary}
          outOfDate={pricingStatus === `out_of_date`}
          marked={stale}
          action={action}
        />
      </Section>
    )
  }

  return (
    <Section id={SECTION_ID}>
      <SectionHeading eyebrow="Your solar investment">
        What you pay, <GradText>line by line</GradText>.
      </SectionHeading>
      {summary ? (
        <SolarPricingCard
          summary={summary}
          financingSummary={financingSummary}
          outOfDate={pricingStatus === `out_of_date`}
          marked={stale}
          action={action}
        />
      ) : (
        <Panel className="flex flex-col items-center gap-4 px-6 py-10 text-center">
          <p
            role="status"
            className="text-[length:var(--step--1)] text-bp-text-muted"
          >
            {PRICING_NOT_AVAILABLE_NOTICE}
          </p>
        </Panel>
      )}
    </Section>
  )
}

/**
 * A financed plan (Credit Human, Concert, Sungage) closes the ledger on what
 * the homeowner finances, never Net system cost (V1), with the monthly
 * payment beside it. Cash keeps Net system cost alone. A Sungage payment
 * carries its program's disclosure: BrightStar's deferred payment or Sunrise
 * Escalator's yearly escalation.
 */
function SolarLedgerFooter({
  summary,
  financingSummary,
}: {
  summary: ProposalFinancialSummaryDto
  financingSummary: SolarFinancingSummaryDto | null
}): JSX.Element {
  // The ledger is never a TPO's, so its terms are a loan's.
  const payment = financingSummary ? (
    <LedgerPayment
      financingSummary={{
        ...financingSummary,
        terms: financingSummary.terms && {
          productLabel: financingSummary.terms.productLabel,
          termYears: financingSummary.terms.termYears,
          apr: financingSummary.terms.apr,
        },
      }}
      unavailableReason={financingSummary.unavailableReason}
      termsFootnote={solarSungagePaymentFootnote(financingSummary.terms)}
    />
  ) : null
  const financed = financingSummary?.financedAmount
  return financed ? (
    <LedgerFooter
      label={FINANCED_AMOUNT_LABELS[financed.kind]}
      amount={financed.amount}
      testId="solar-financed-amount"
    >
      {payment}
    </LedgerFooter>
  ) : (
    <NetSystemCostFooter netSystemCost={summary.netSystemCost}>
      {payment}
    </NetSystemCostFooter>
  )
}

function SolarPricingCard({
  summary,
  financingSummary,
  outOfDate,
  marked,
  action,
}: {
  summary: ProposalFinancialSummaryDto
  financingSummary: SolarFinancingSummaryDto | null
  outOfDate: boolean
  marked: boolean
  action: ReactNode
}): JSX.Element {
  const financingTerms = financingSummary?.terms ?? null
  const sungage = financingTerms?.sungage ?? null
  const terms =
    financingSummary && financingTerms
      ? financingTermsLabel({
          productLabel: sungage
            ? `${financingSummary.lender} ${sungage.fundingGroupLabel}`
            : financingSummary.lender,
          termYears: financingTerms.termYears,
          apr: financingTerms.apr,
        })
      : null
  return (
    <SummaryCardLayout
      testId="solar-financial-summary"
      // A stale ledger stays visible on screen but never prints: a printed
      // proposal must not hand the homeowner superseded figures.
      className={outOfDate ? `print:hidden` : undefined}
      ledger={
        <>
          <div
            data-testid={marked ? `solar-financial-summary-stale` : undefined}
            className={marked ? STALE_FIGURES_WITHIN_CLASS : undefined}
          >
            {marked ? <p className="sr-only">{STALE_FIGURES_NOTE}</p> : null}
            <SummaryHeading>
              {terms ? (
                <FinancingTermsLine
                  label={terms}
                  printable={financingSummary?.status === `current`}
                  testId="solar-financing-terms"
                />
              ) : null}
            </SummaryHeading>
            <LedgerRows lines={solarLedgerLines(summary, financingSummary)} />
            <SolarLedgerFooter
              summary={summary}
              financingSummary={financingSummary}
            />
            {financingSummary && financingTerms && sungage ? (
              <SolarSungageLoanTerms
                lender={financingSummary.lender}
                terms={{ ...financingTerms, sungage }}
                printable={financingSummary.status === `current`}
              />
            ) : null}
          </div>
          {action}
        </>
      }
      incentivesTestId="solar-financial-summary-incentives"
      incentives={
        hasFinancialSummaryIncentives(summary) ? (
          <div>
            <FinancialSummaryIncentives
              summary={summary}
              totalTestId="solar-total-incentives"
            />
          </div>
        ) : null
      }
    />
  )
}
